import {useState} from 'react';
import {Alert, Button, Dialog, Input} from '@vector';
import {login} from '../homeService';
import {getRecaptchaToken} from '../../portal/recaptcha';
import ForgotPassword from './ForgotPassword';

/**
 * Sign-in, in a dialog off the header button.
 *
 * Posts to `/web/ui/brandAuth/public/login` — an endpoint that already existed and already does the hard
 * parts: reCAPTCHA, establishing the session through `userLoginService.loginUser`, and mapping every
 * `UserStatus` including external-auth redirects, lockout with a retry time, and the brand's own failure
 * wording. Writing a second login endpoint for this page would have been the riskiest thing in the
 * rewrite and was not necessary.
 *
 * **Two-step mode is now client state.** On the Struts page each step is a full POST and a re-render:
 * `LoginAction` sets `showPassword` when the service answers `PASSWORD_REQUIRED` and returns `INPUT`, so
 * the entire homepage is rebuilt to swap one input for another. Here it is a piece of state, and the step
 * change is announced through `aria-live` so it is not a silent swap for a screen-reader user.
 */
export default function SignIn({useTwoStepLogin}) {
    const [open, setOpen] = useState(false);
    /*
     * Derived, not copied into state. `useTwoStepLogin` arrives from the config call *after* the first
     * render, so seeding state from it left the dialog showing both fields on a brand that asks for the
     * username first - the initial value was captured while the flag was still its default.
     */
    const [passwordRequested, setPasswordRequested] = useState(false);
    const showPassword = !useTwoStepLogin || passwordRequested;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // Opened straight away by a link to #forgot-password, e.g. the old /home/southafrica/forgotPassword page,
    // which redirects to /#forgot-password (urlrewrite.xml).
    const [forgotOpen, setForgotOpen] = useState(
        () => typeof window !== 'undefined' && window.location.hash === '#forgot-password');

    const close = () => {
        setOpen(false);
        setError(null);
        setPassword('');
        setPasswordRequested(false);
    };

    const submit = async (event) => {
        event.preventDefault();

        // Step one of two: no credentials have been offered yet, so there is nothing to ask the server.
        if (useTwoStepLogin && !showPassword) {
            if (username.trim()) {
                setPasswordRequested(true);
            }
            return;
        }

        setBusy(true);
        setError(null);

        try {
            const recaptchaToken = await getRecaptchaToken('login');
            const result = await login({username: username.trim(), password, recaptchaToken});

            /*
             * BrandLoginResultBean is {loggedIn, redirectUrl, errorMessage} - there is no `success`
             * field, and `redirect()` sets only redirectUrl, leaving loggedIn false. So a successful
             * sign-in looks like {loggedIn: false, redirectUrl: "/dashboard/..."}: the redirect URL is
             * what says it worked, not the boolean.
             */
            if (result.redirectUrl) {
                window.location.href = result.redirectUrl;
                return;
            }
            if (result.loggedIn) {
                // Signed in with nowhere to send them - the Checkpoints case. Reload into the app.
                window.location.reload();
                return;
            }
            setError(result.errorMessage || 'We could not sign you in. Please check your details.');
        } catch (e) {
            setError('We could not sign you in. Please try again in a moment.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button onClick={() => setOpen(true)}>Sign in</Button>

            <Dialog
                title="Sign in"
                open={open}
                onCancel={close}
                footer={null}
                destroyOnHidden>
                <form onSubmit={submit}>
                    <div aria-live="polite">
                        {error && <Alert type="error" message={error} showIcon
                                         className="ch-site-alert"/>}
                    </div>

                    <label className="ch-site-field-label" htmlFor="ch-home-username">Username</label>
                    <Input
                        id="ch-home-username"
                        autoComplete="username"
                        required
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />

                    {showPassword && (
                        <>
                            <label className="ch-site-field-label" htmlFor="ch-home-password">
                                Password
                            </label>
                            {/* Focused only when it appears as step two. When both fields show at once, the
                                later autoFocus would win and skip the username. */}
                            <Input.Password
                                id="ch-home-password"
                                autoComplete="current-password"
                                required
                                autoFocus={Boolean(useTwoStepLogin)}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </>
                    )}

                    <div className="ch-site-actions">
                        <Button type="primary" htmlType="submit" loading={busy} block>
                            {showPassword ? 'Sign in' : 'Next'}
                        </Button>
                    </div>

                    <p className="ch-site-hint">
                        {/* A button, not a link: it opens a dialog rather than going anywhere, and a
                            screen reader should be told which. */}
                        <Button type="link" onClick={() => setForgotOpen(true)}>
                            Forgotten your password?
                        </Button>
                    </p>
                </form>
            </Dialog>

            <ForgotPassword open={forgotOpen} onClose={() => setForgotOpen(false)}/>
        </>
    );
}
